// src/components/ThreeRowTiltedMarquee.jsx
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { motion, useAnimationFrame, useMotionValue } from "framer-motion";

/**
 * UltraFast ThreeRowTiltedMarquee
 * - Virtualized tiles (only render what fills the row + 1)
 * - Eager-load first image per row (fetchpriority=high)
 * - Mount duplicate loop only after first paint
 * - Pause when offscreen / tab hidden
 */
export default function ThreeRowTiltedMarquee({
  row1Images = defaultRow1,
  row2Images = defaultRow2,
  row3Images = defaultRow3,
  height = 1000,
  background = "#000",
  rowOverlap = 30,
  row1 = { speed: 70, tilt: 30, roll: 2, direction: "left" },
  row2 = { speed: 92, tilt: -30, roll: 3, direction: "right" },
  row3 = { speed: 62, tilt: 30, roll: 4, direction: "left" },
}) {
  const containerRef = useRef(null);
  const [inView, setInView] = useState(true);

  // Pause animation if offscreen or tab hidden
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => setInView(entries[0]?.isIntersecting ?? true),
      { threshold: 0 }
    );
    io.observe(el);
    const onVis = () => setInView(!document.hidden);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      io.disconnect();
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        width: "100%",
        height,
        overflow: "hidden",
        background,
        gap: 2,
        display: "flex",
        flexDirection: "column",
        contain: "layout paint",
      }}
    >
      <Row images={row1Images} {...row1} style={{ flex: 1, marginBottom: rowOverlap }} play={inView} />
      <Row images={row2Images} {...row2} style={{ flex: 1, marginBottom: rowOverlap }} play={inView} />
      <Row images={row3Images} {...row3} style={{ flex: 1 }} play={inView} />

      {/* edge fade */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background:
            "linear-gradient(90deg, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0) 10%, rgba(0,0,0,0) 90%, rgba(0,0,0,0.75) 100%)",
        }}
      />
    </div>
  );
}

function Row({
  images,
  speed = 80,
  direction = "left",
  gap = 8,
  radius = 14,
  tilt = 28,
  roll = 0,
  perspective = 900,
  scale = 1,
  itemWidth = 420,
  itemAspect = 1.6,
  style,
  play = true,
}) {
  const rowRef = useRef(null);
  const x = useMotionValue(0);
  const [loopW, setLoopW] = useState(0);
  const [primeLoaded, setPrimeLoaded] = useState(false);
  const [rowW, setRowW] = useState(0);

  const itemH = itemWidth / itemAspect;

  // Virtualize: only render enough tiles to fill the width (+1)
  const tilesNeeded = useMemo(() => {
    if (!rowW) return images.length;
    return Math.max(Math.ceil(rowW / (itemWidth + gap)) + 1, 2);
  }, [rowW, itemWidth, gap, images.length]);

  const virtualImages = useMemo(() => {
    const out = [];
    for (let i = 0; i < tilesNeeded; i++) out.push(images[i % images.length]);
    return out;
  }, [images, tilesNeeded]);

  useLayoutEffect(() => {
    const w = virtualImages.length * itemWidth + (virtualImages.length - 1) * gap;
    setLoopW(w);
  }, [virtualImages, itemWidth, gap]);

  useLayoutEffect(() => {
    const el = rowRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => setRowW(entries[0].contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useAnimationFrame((t, delta) => {
    if (!play || loopW <= 0) return;
    const dir = direction === "left" ? -1 : 1;
    const dx = dir * speed * (delta / 1000);
    let next = x.get() + dx;
    if (direction === "left" && next <= -loopW) next += loopW;
    if (direction === "right" && next >= 0) next -= loopW;
    x.set(next);
  });

  return (
    <div ref={rowRef} style={{ position: "relative", width: "100%", height: "100%", ...style }}>
      <div
        style={{
          position: "absolute",
          inset: 0,
          transformStyle: "preserve-3d",
          transformOrigin: "center",
          transform: `perspective(${perspective}px) rotateY(${tilt}deg) rotateZ(${roll}deg) scale(${scale}) translateZ(0)`,
          overflow: "visible",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            overflow: "visible",
          }}
        >
          <motion.div
            style={{ display: "flex", gap, x, willChange: "transform", transform: "translate3d(0,0,0)" }}
          >
            <Sequence
              images={virtualImages}
              w={itemWidth}
              h={itemH}
              r={radius}
              gap={gap}
              eagerFirst
              onFirstLoaded={() => setPrimeLoaded(true)}
            />
            {primeLoaded && (
              <Sequence images={virtualImages} w={itemWidth} h={itemH} r={radius} gap={gap} />
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
}

function Sequence({ images, w, h, r, gap = 0, eagerFirst = false, onFirstLoaded }) {
  return (
    <ul style={{ display: "flex", listStyle: "none", padding: 0, margin: 0, gap }}>
      {images.map((src, i) => (
        <li key={`${src}-${i}`} style={{ flex: "0 0 auto" }}>
          <div
            style={{
              width: w,
              height: h,
              borderRadius: r,
              position: "relative",
              overflow: "hidden",
              background: "#101010",
              boxShadow: "0 10px 24px rgba(0,0,0,0.3), 0 4px 10px rgba(0,0,0,0.2)",
              contain: "layout paint",
            }}
          >
            <img
              src={src}
              alt=""
              decoding="async"
              loading={eagerFirst && i === 0 ? "eager" : "lazy"}
              fetchpriority={eagerFirst && i === 0 ? "high" : "auto"}
              onLoad={eagerFirst && i === 0 ? onFirstLoaded : undefined}
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "center",
                background: "linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))",
                transform: "translateZ(0)",
              }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}

/* === Your 10 AVIF files distributed across 3 rows === */
const defaultRow1 = ["/images/1.avif", "/images/2.avif", "/images/3.avif", "/images/4.avif"];
const defaultRow2 = ["/images/5.avif", "/images/6.avif", "/images/7.avif"];
const defaultRow3 = ["/images/8.avif", "/images/9.avif", "/images/10.avif"];
